import { createSignal, createEffect, onCleanup, Show } from 'solid-js';

export interface LoadingOverlayProps {
  message?: string;
  class?: string;
  fullScreen?: boolean;
  /** Spinner only — no full overlay card (for table sections). */
  compact?: boolean;
  progress?: number | null; // 0-100 or null for indeterminate
  showProgress?: boolean;
  progressMessage?: string;
  type?: "spinner" | "dots" | "pulse" | "skeleton";
  containerStyle?: string;
}

export default function LoadingOverlay(props: LoadingOverlayProps) {
  const message = () => props.message ?? "Loading...";
  const className = () => props.class ?? "";
  const fullScreen = () => props.fullScreen ?? false;
  const compact = () => props.compact === true;
  const progress = () => props.progress;
  const showProgress = () => props.showProgress ?? false;
  const progressMessage = () => props.progressMessage ?? "";
  const type = () => props.type ?? "spinner";
  const containerStyle = () => props.containerStyle ?? "";

  const currentProgress = () => {
    const progressValue = progress();
    return progressValue !== null && progressValue !== undefined ? progressValue : 0;
  };

  const isIndeterminate = () => {
    const progressValue = progress();
    return progressValue === null || progressValue === undefined;
  };

  const [autoProgressInterval, setAutoProgressInterval] = createSignal<ReturnType<typeof setInterval> | null>(null);

  createEffect(() => {
    const existingInterval = autoProgressInterval();
    if (existingInterval) {
      clearInterval(existingInterval);
      setAutoProgressInterval(null);
    }
  });

  onCleanup(() => {
    const existingInterval = autoProgressInterval();
    if (existingInterval) {
      clearInterval(existingInterval);
    }
  });

  const containerClass = () => {
    if (compact()) {
      return `app-loading-overlay app-loading-overlay--compact ${className()}`.trim();
    }
    const position = fullScreen() ? "app-loading-overlay--fullscreen" : "app-loading-overlay--embedded";
    return `app-loading-overlay ${position} ${className()}`.trim();
  };

  const renderSpinner = () => <div class="app-loading-spinner" role="status" aria-label="Loading" />;

  const renderDots = () => (
    <div class="app-loading-dots" role="status" aria-label="Loading">
      <div class="app-loading-dot" />
      <div class="app-loading-dot" />
      <div class="app-loading-dot" />
    </div>
  );

  const renderPulse = () => <div class="app-loading-pulse-disc" role="status" aria-label="Loading" />;

  const renderSkeleton = () => (
    <div class="app-loading-skeleton" role="status" aria-label="Loading">
      <div class="app-loading-skeleton-line" />
      <div class="app-loading-skeleton-line app-loading-skeleton-line--medium" />
      <div class="app-loading-skeleton-line app-loading-skeleton-line--short" />
    </div>
  );

  const renderLoader = () => {
    switch (type()) {
      case 'dots': return renderDots();
      case 'pulse': return renderPulse();
      case 'skeleton': return renderSkeleton();
      default: return renderSpinner();
    }
  };

  const showMessage = () => {
    const msg = message();
    return msg.length > 0 && (!compact() || msg !== "Loading...");
  };

  if (compact()) {
    return (
      <div class={containerClass()} style={containerStyle()}>
        <div class="app-loading-compact">
          {renderLoader()}
          <Show when={showMessage()}>
            <p class="app-loading-message">{message()}</p>
          </Show>
        </div>
      </div>
    );
  }

  return (
    <div class={containerClass()} style={containerStyle()}>
      <div class="app-loading-panel">
        <Show when={!showProgress() || isIndeterminate()}>
          {renderLoader()}
        </Show>

        <Show when={showMessage()}>
          <p class="app-loading-message">{message()}</p>
        </Show>

        <Show when={showProgress() && !isIndeterminate()}>
          <div class="app-loading-progress-block">
            <div class="app-loading-progress-track">
              <div
                class="app-loading-progress-fill"
                style={`width: ${Math.max(0, Math.min(100, currentProgress()))}%;`}
              />
            </div>
            <div class="app-loading-progress-meta">
              <span>{progressMessage() || `${Math.round(currentProgress())}% complete`}</span>
              <span>{Math.round(currentProgress())}%</span>
            </div>
          </div>
        </Show>

        <Show when={showProgress() && isIndeterminate()}>
          <div class="app-loading-progress-block">
            <div class="app-loading-progress-track">
              <div class="app-loading-progress-pulse" />
            </div>
            <p class="app-loading-progress-status">Processing...</p>
          </div>
        </Show>
      </div>
    </div>
  );
}
